/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { FieldGroup, FieldModeButton, ISelectOption_Item, TSelectOption } from "@adapptio/lib-editor-ui";
import {
	ISchemaValueModel,
	isSchemaConstStringModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { getSchemaDefaultIcon } from "../../../ui/components/Inspector/InspectorHelpers";
import { isNil } from "@adapptio/lib-shared";
import { IInspectorControl_ValueType, TInspectorControlProps } from "../InspectorControlTypes";

export interface IInspectorControlValueTypeProps
	extends TInspectorControlProps<IInspectorControl_ValueType> {}

export const InspectorControlValueType: React.FC<IInspectorControlValueTypeProps> = ({ models }) => {
	const options: TSelectOption[] = [];

	const getModeChangeHandler = useCallback<
		(
			model: ISchemaValueModel<TGenericBlueprintSchema>,
			type: SCHEMA_VALUE_TYPE
		) => ISelectOption_Item["onClick"]
	>(
		(model, type) => () => {
			model.schema.setType(model, type, true);
		},
		[]
	);

	for (let i = 0; i < models.length; i++) {
		const model = models[i];

		options.push({
			id: SCHEMA_VALUE_TYPE.CONST,
			value: SCHEMA_VALUE_TYPE.CONST,
			icon:
				model.model.schema.opts.icon ||
				model.model.schema.constSchema.opts.icon ||
				getSchemaDefaultIcon(model.model.schema, null) ||
				getSchemaDefaultIcon(model.model.schema?.constSchema),
			label: "Constant value",
			onClick: getModeChangeHandler(model.model, SCHEMA_VALUE_TYPE.CONST)
		});

		if (isNil(model.model.schema.opts.allowExpression) || model.model.schema.opts.allowExpression) {
			options.push({
				id: SCHEMA_VALUE_TYPE.EXPRESSION,
				value: SCHEMA_VALUE_TYPE.EXPRESSION,
				icon: "mdi/function-variant",
				label: "Dynamic value",
				onClick: getModeChangeHandler(model.model, SCHEMA_VALUE_TYPE.EXPRESSION)
			});
		}

		if (
			(isNil(model.model.schema.opts.allowTranslate) || model.model.schema.opts.allowTranslate) &&
			isSchemaConstStringModel(model.model)
		) {
			options.push({
				id: SCHEMA_VALUE_TYPE.TRANSLATE,
				value: SCHEMA_VALUE_TYPE.TRANSLATE,
				icon: "mdi/translate",
				label: "Translated value",
				onClick: getModeChangeHandler(model.model, SCHEMA_VALUE_TYPE.TRANSLATE)
			});
		}
	}

	return (
		<FieldGroup preventTruncate>
			<FieldModeButton options={options} value={models[0].model.type} />
		</FieldGroup>
	);
};
